/**
 * Katera bližnjica pripada tipki (P2 §8.8) — **čista odločitev, brez DOM**.
 *
 * Ločena od `ui/keyboard.ts` zato, ker so tu vsa pravila, ki jih je mogoče
 * zgrešiti, in nobeno od njih ne rabi brskalnika, da bi ga preverili.
 */

export type Shortcut = 'toggle' | 'previous' | 'next' | 'volumeUp' | 'volumeDown' | 'mute';

export interface KeyIntent {
  key: string;
  /** Kateri koli modifikator. Bližnjica z modifikatorjem pripada sistemu. */
  modified: boolean;
  /** Sredi sestavljanja znaka (IME) — tipka še ni tipka. */
  composing: boolean;
  /** Fokus je v polju: presledek napiše presledek, `m` napiše `m`. */
  typing: boolean;
  /** Fokus je na gumbu ali povezavi: presledek pripada njemu. */
  activatable: boolean;
  /** Fokus je v seznamu zavihkov: `←` in `→` pripadata njemu. */
  inTablist: boolean;
  /** Kar koli je v fokusu — takrat puščici drsita po strani. */
  focused: boolean;
}

/**
 * ⛔ **Tri pravila, brez katerih bližnjice škodijo bolj, kot koristijo.**
 *
 * **1 · Med tipkanjem ne velja nobena.** Presledek sredi iskalnega polja mora
 * napisati presledek, ne ustaviti zvoka.
 *
 * **2 · Tipka, ki pripada elementu v fokusu, ostane njegova.** Ko je fokus na
 * `<button>` ali povezavi, je presledek **njegova** tipka — prestrezanje bi
 * pomenilo, da tipkovnični uporabnik ne more sprožiti tistega, kar je pravkar
 * izbral. Isto velja za `←` `→` v seznamu zavihkov (`role="tablist"`): tam sta
 * **predpisan** način premikanja med zavihki (APG), zato menjava postaje
 * takrat odstopi. Pravilo je eno, primera sta dva.
 *
 * **3 · `↑` `↓` samo, kadar fokusa ni nikjer.** To je najpomembnejše in ni
 * očitno: puščici sta **osnovni način drsenja po strani**. Če ju vzamemo
 * globalno, tipkovnični uporabnik ne more več prebrskati seznama postaj —
 * hujša napaka od manjkajoče bližnjice za glasnost (WCAG 2.1.1). Glasnost je
 * poleg tega dosegljiva z vidnim drsnikom, `M` pa deluje vedno.
 */
export function shortcutFor(intent: KeyIntent): Shortcut | null {
  if (intent.modified || intent.composing || intent.typing) return null;

  switch (intent.key) {
    // `Spacebar` je zapis starejših brskalnikov; stane eno vrstico.
    case ' ':
    case 'Spacebar':
      return intent.activatable ? null : 'toggle';

    case 'ArrowLeft':
      return intent.inTablist ? null : 'previous';

    case 'ArrowRight':
      return intent.inTablist ? null : 'next';

    case 'ArrowUp':
      return intent.focused ? null : 'volumeUp';

    case 'ArrowDown':
      return intent.focused ? null : 'volumeDown';

    // `key` in ne `code`: bližnjica je poimenovana s črko, ki jo uporabnik
    // vidi na tipki — na drugih razporeditvah je fizična tipka drugje.
    case 'm':
    case 'M':
      return 'mute';

    default:
      return null;
  }
}
